import { PAGES, GAME_TYPES } from "@/common/consts";

import DynamicPage from "@/pages/dynamic-page"; // Avoid code splitting in this page.
import newGamePages from "./new-game-pages";

const coreRoutes = [
	{
		path: "/",
		name: PAGES.HOME,
		component: () => import(/* webpackChunkName: "home" */ "@/pages/home"),
	},
	{
		path: "/app/about/",
		name: PAGES.APP_ABOUT,
		component: () =>
			import(/* webpackChunkName: "about-app" */ "@/pages/about-app"),
		meta: {
			breadcrumb: [{ label: "About Word Finder" }],
		},
	},
	{
		path: "/app/privacy-policy/",
		name: PAGES.APP_PRIVACY_POLICY,
		component: () =>
			import(/* webpackChunkName: "legal-info" */ "@/pages/legal-info"),
		meta: {
			breadcrumb: [{ label: "APP Privacy Policy" }],
		},
	},
	{
		path: "/4pics1word/",
		name: PAGES.FOURPICS,
		component: () =>
			import(/* webpackChunkName: "fourpics" */ "@/pages/fourpics"),
		meta: {
			breadcrumb: [{ label: "4 Pics 1 Word®" }],
		},
	},
	{
		path: "/4pics1word/:wordlength/:tiles",
		name: PAGES.FOURPICS_RESULTS,
		component: () =>
			import(/* webpackChunkName: "results" */ "@/pages/results-en-new"),
		meta: {
			breadcrumb: [
				{ label: "4 Pics 1 Word®", url: "/4pics1word/" },
				{ label: "Results" },
			],
			gameType: GAME_TYPES.UNSCRAMBLE,
		},
	},
	{
		path: "/scrabble-dictionary/:tiles",
		name: PAGES.SCRABBLE_DICT_RESULTS,
		component: () =>
			import(
				/* webpackChunkName: "results-en-new" */ "@/pages/results-en-new"
			),
		meta: {
			breadcrumb: [
				{ label: "Scrabble Dictionary", url: "/scrabble-dictionary/" },
				{ label: "Results" },
			],
			gameType: GAME_TYPES.UNSCRAMBLE,
		},
	},
	{
		path: "/unscramble/:tiles",
		name: PAGES.UNSCRAMBLER_RESULTS,
		component: () =>
			import(
				/* webpackChunkName: "results-en-new" */ "@/pages/results-en-new"
			),
		meta: {
			breadcrumb: [
				{ label: "Unscramble", url: "/unscramble/" },
				{ label: "Results" },
			],
			gameType: GAME_TYPES.UNSCRAMBLE,
		},
	},
	{
		path: "/letter-words/",
		name: PAGES.WORDS_BY_LENGTH_INDEX,
		component: () =>
			import(/* webpackChunkName: "words-lists" */ "@/pages/words-lists"),
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
			],
			content: {
				linkLabel: "Letter Words",
				pageTitle: "Words by Letter Length",
			},
		},
	},
	{
		path: "/letter-words/:length/",
		name: PAGES.WORDS_BY_LENGTH_RESULTS,
		component: () =>
			import(
				/* webpackChunkName: "results-en-new" */ "@/pages/results-en-new"
			),
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
			],
			endpoint: "length",
			gameType: GAME_TYPES.WORDSLISTS,
		},
	},
	{
		path: "/consonants/",
		name: PAGES.WORDS_CONSONANTS,
		component: () =>
			import(
				/* webpackChunkName: "results-en-new" */ "@/pages/results-en-new"
			),
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
				{ label: "Consonants" },
			],
			endpoint: "consonants",
			gameType: GAME_TYPES.WORDSLISTS,
		},
	},
	{
		path: "/ending-with/",
		name: PAGES.WORDS_ENDING_INDEX,
		component: () =>
			import(/* webpackChunkName: "words-lists" */ "@/pages/words-lists"),
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
			],
			content: {
				linkLabel: "Words Ending in",
				pageTitle: "Words Ending in…",
			},
		},
	},
	{
		path: "/ending-in-ing/",
		name: PAGES.WORDS_ENDING_ING,
		component: () =>
			import(
				/* webpackChunkName: "results-en-new" */ "@/pages/results-en-new"
			),
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
				{ label: "Words Ending in ING" },
			],
			endpoint: "ing",
			gameType: GAME_TYPES.WORDSLISTS,
		},
	},
	{
		path: "/ending-with/:letter/",
		name: PAGES.WORDS_ENDING_RESULTS,
		component: () =>
			import(
				/* webpackChunkName: "results-en-new" */ "@/pages/results-en-new"
			),
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
			],
			content: {
				pageTitle: "Words Ending in",
			},
			endpoint: "end-with",
			gameType: GAME_TYPES.WORDSLISTS,
		},
	},
	{
		path: "/words-with-the-letter/",
		name: PAGES.WORDS_HAVING_INDEX,
		component: () =>
			import(/* webpackChunkName: "words-lists" */ "@/pages/words-lists"),
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
			],
			content: {
				linkLabel: "Words with the Letter",
				pageTitle: "Words with the Letter…",
			},
		},
	},
	{
		path: "/words-with-the-letter/:letter/",
		name: PAGES.WORDS_HAVING_RESULTS,
		component: () =>
			import(
				/* webpackChunkName: "results-en-new" */ "@/pages/results-en-new"
			),
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
			],
			content: {
				pageTitle: "Words with the Letter",
			},
			endpoint: "words-with",
			gameType: GAME_TYPES.WORDSLISTS,
		},
	},
	{
		path: "/index/",
		name: PAGES.WORDS_LISTS_INDEX,
		component: () =>
			import(
				/* webpackChunkName: "wordlistsindex" */ "@/pages/word-lists-index"
			),
		meta: {
			breadcrumb: [{ label: "Word Lists for Word Games" }],
		},
	},
	{
		path: "/words-that-start/",
		name: PAGES.WORDS_STARTING_INDEX,
		component: () =>
			import(/* webpackChunkName: "words-lists" */ "@/pages/words-lists"),
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
			],
			content: {
				linkLabel: "Words that start with the Letter",
				pageTitle: "Words that start with…",
			},
		},
	},
	{
		path: "/words-that-start/:letter/",
		name: PAGES.WORDS_STARTING_RESULTS,
		component: () =>
			import(
				/* webpackChunkName: "results-en-new" */ "@/pages/results-en-new"
			),
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
			],
			content: {
				pageTitle: "Words that start with",
			},
			endpoint: "start-with",
			gameType: GAME_TYPES.WORDSLISTS,
		},
	},
	{
		path: "/vowels/",
		name: PAGES.WORDS_VOWELS,
		component: () =>
			import(
				/* webpackChunkName: "results-en-new" */ "@/pages/results-en-new"
			),
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
				{ label: "Vowels" },
			],
			endpoint: "mostly-vowels",
			gameType: GAME_TYPES.WORDSLISTS,
		},
	},
	{
		path: "/q-no-u/",
		name: PAGES.WORDS_WITH_Q_NO_U,
		component: () =>
			import(
				/* webpackChunkName: "results-en-new" */ "@/pages/results-en-new"
			),
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
				{ label: "Words with Q no U" },
			],
			endpoint: "q-no-u",
			gameType: GAME_TYPES.WORDSLISTS,
		},
	},
	{
		path: "/abexp/:channel?",
		name: "experiment-override",
		component: () =>
			import(
				/* webpackChunkName: "experiment-override" */ "@/pages/experiment-override"
			),
	},
	{
		path: "/feature-toggling",
		name: "feature-toggling",
		component: () =>
			import(
				/* webpackChunkName: "feature-toggling" */ "@/pages/feature-toggling"
			),
	},
	{
		path: "/game-cheats/",
		name: PAGES.GAME_CHEATS,
		component: () =>
			import(/* webpackChunkName: "game-cheats" */ "@/pages/game-cheats"),
		meta: {
			breadcrumb: [{ label: "Game Cheats" }],
		},
	},
	...newGamePages,
	{
		// Dynamic page:
		path: "/dynamic-page/",
		name: PAGES.DYNAMIC_PAGE,
		component: DynamicPage,
	},
	{
		// Dynamic results page
		path: "/dynamic-results-page/",
		name: PAGES.DYNAMIC_RESULTS_PAGE,
		component: () =>
			import(
				/* webpackChunkName: "results-en-new" */ "@/pages/results-en-new"
			),
	},
	{
		path: "/blog/",
		name: PAGES.ARTICLE_INDEX,
		component: () =>
			import(
				/* webpackChunkName: "article-index" */ "@/pages/articles/index"
			),
	},
	{
		path: "/blog/:slug",
		name: PAGES.ARTICLE_DETAIL,
		component: () =>
			import(
				/* webpackChunkName: "article-detail" */ "@/pages/articles/detail"
			),
	},
	{
		path: "/saved-words/",
		name: PAGES.SAVED_WORDS,
		component: () =>
			import(/* webpackChunkName: "saved-words" */ "@/pages/saved-words"),
	},
	{
		path: "/app/get-the-app/",
		name: PAGES.GET_THE_APP,
		component: () =>
			import(/* webpackChunkName: "get-the-app" */ "@/pages/get-app"),
		meta: {
			breadcrumb: [{ label: "Get The App" }],
		},
	},
	{
		// Soft 404 Pages:
		path: "*",
		name: PAGES.NOT_FOUND,
		component: () =>
			import(
				/* webpackChunkName: "page-not-found" */ "@/pages/page-not-found"
			),
	},
];

const redirections = [
	{
		path: "/app",
		redirect: {
			name: PAGES.APP_ABOUT,
		},
	},
	{
		path: "/q_no_u",
		redirect: {
			name: PAGES.WORDS_WITH_Q_NO_U,
		},
	},
	{
		path: "/wordcookies",
		redirect: {
			name: PAGES.WORDCOOKIES,
		},
	},
	{
		// [WFW-286] Kept for backwards compatibility:
		path: "/unscramble/remember_tiles/:tiles",
		redirect: {
			name: PAGES.UNSCRAMBLER_RESULTS,
		},
	},
	{
		// [WFW-286] Kept for backwards compatibility:
		path: "/scrabble-dictionary/exact-match/:tiles",
		redirect: {
			name: PAGES.SCRABBLE_DICT_RESULTS,
		},
	},
];

// List of routes (sorted)
export const routes = coreRoutes.concat(redirections);

// Map of routes accessible by name (this is meant for unit testing purposes where it's useful to be able to pick routes by its name)
export const routesMap = coreRoutes.reduce(function(map, route) {
	map[route.name] = route;
	return map;
}, {});
